import type {
	BabyBjornCart,
	BabyBjornOrderType,
} from "@babybjorn/utils/commercetools/custom-types/babybjorn-order";
import {
	nonMessageableProducts,
	nonWrappableProducts,
} from "@babybjorn/utils/gifting-options";
import { isObjectKey } from "@babybjorn/utils/typeguards/objects";
import type {
	AddressDraft,
	Cart,
	ProductProjection,
	ShippingMethod,
} from "@commercetools/platform-sdk";
import is from "@sindresorhus/is";
import type { TCountryCode } from "countries-list";
import { useCallback, useEffect, useRef } from "react";
import { AppDispatchContext, useAppState } from "../context/app/context";
import {
	CheckoutDispatchContext,
	useCheckoutState,
} from "../context/checkout/context";
import { sendBrowserEvent } from "../utils/browser-events";
import { getOrderSummaryPrices } from "../utils/cart/money";
import { getAttribute } from "../utils/commercetools/attributes";
import {
	isBundleItemData,
	type BundleItemData,
} from "../utils/commercetools/bundle-item";
import * as CartService from "../utils/commercetools/cart-service";
import { isCommercetoolsHttpResponseError } from "../utils/commercetools/fetch-from-ct";
import type { ExpandedOrder } from "../utils/commercetools/order-service";
import { getGiftOptionsVariant } from "../utils/commercetools/variant-service";
import {
	isGiftCardData,
	isGiftMessageData,
	type GiftCardData,
	type GiftMessageData,
} from "../utils/gift-card";
import { pushCustomGTMEvent } from "../utils/gtm/gtm-handlers";
import LocalStorage from "../utils/local-storage";
import { useDispatchContext } from "./use-dispatch-context";
import { useUserLocation } from "./use-user-location";

type GiftCardCustomLineItemFields = GiftCardData;
type GiftMessageCustomLineItemFields = GiftMessageData;
type BundleItemCustomLineItemFields = BundleItemData;
// Define more custom line item types like GiftCardCustomLineItemFields and add here if needed when adding line items.
export type CustomLineItemData =
	| GiftCardCustomLineItemFields
	| GiftMessageCustomLineItemFields
	| BundleItemCustomLineItemFields;

type AddItemsToCartItemProps = {
	product: Queries.CommerceToolsProductFragment;
	variant: Queries.ProductVariantFragment;
	additionalData?: CustomLineItemData;
	quantity?: number;
};
interface CartServiceMethods {
	updateCartLineItemQuantity: (
		lineItemId: string,
		quantity: number,
	) => Promise<void>;
	removeLineItemFromCart: (lineItemId: string) => Promise<void>;
	addItemsToCart: (
		items: AddItemsToCartItemProps[],
		showToastOnAdd?: boolean,
	) => Promise<void>;
	addGiftBoxItemToCart: (giftBoxProduct: ProductProjection) => Promise<void>;
	addGiftMessageItemToCart: (
		giftMessageProduct: ProductProjection,
	) => Promise<void>;
	updateGiftMessageItemInCart: (
		giftMessageProduct: ProductProjection,
		giftMessageText: string,
	) => Promise<void>;
	addCartAddresses: (
		shippingAddress?: AddressDraft,
		billingAddress?: AddressDraft,
	) => Promise<void>;
	initExistingCart: () => Promise<void>;
	addCartPayment: (paymentId: string) => Promise<void>;
	removeCartPayment: (paymentId: string) => Promise<void>;
	addCartDiscountCode: (code: string) => Promise<void>;
	removeCartDiscountCode: () => Promise<void>;
	setShippingMethod: (shippingMethod: ShippingMethod) => Promise<void>;
	setCartCustomField: (
		key: keyof BabyBjornOrderType["fields"],
		value: unknown,
	) => Promise<void>;
	createOrderFromCart: () => Promise<ExpandedOrder>;
	refreshCart: () => Promise<void>;
	getSummaryPrices: () => Promise<ReturnType<typeof getOrderSummaryPrices>>;
	cart: BabyBjornCart | undefined;
	cartLoading: boolean;
	hasCart: boolean;
}

const customLineItemsFieldsTypeCheck = new Map<
	string,
	(
		data: unknown,
	) => data is
		| GiftCardCustomLineItemFields
		| GiftMessageCustomLineItemFields
		| BundleItemCustomLineItemFields
>([
	[
		"gift-card",
		(data: unknown): data is GiftCardCustomLineItemFields => {
			return isGiftCardData(data);
		},
	],
	[
		"gift-message",
		(data: unknown): data is GiftMessageCustomLineItemFields => {
			return isGiftMessageData(data);
		},
	],
	[
		"bundle-item",
		(data: unknown): data is BundleItemCustomLineItemFields => {
			return isBundleItemData(data);
		},
	],
]);

const customLineItemMap = new Map<string, string>([
	["gift-card", "gift-card-line-item"],
	["gift-message", "gift-message-line-item"],
	["bundle-item", "bundle-line-item"],
]);

let countryUpdateInProgress = false;

/**
 * A service that exposes functionality related to the cart such as adding, removing items in the cart,
 * changing quantities, removing the cart, creating the cart etc.
 */
export const useCartService = (): CartServiceMethods => {
	const { siteConfig, userCountry } = useAppState();
	const checkoutState = useCheckoutState();
	const siteConfigStore = useRef(siteConfig.store);
	const { data: userLocationData } = useUserLocation();

	const appDispatch = useDispatchContext(AppDispatchContext);
	const checkoutDispatch = useDispatchContext(CheckoutDispatchContext);

	const validCart = useCallback(
		(cart?: Cart): cart is Cart =>
			!!cart &&
			cart.store?.key === siteConfigStore.current &&
			cart.cartState !== "Ordered",
		[],
	);

	const loadCartFromLocalStorage = useCallback(async () => {
		const cartId = LocalStorage.get("CT_CART_ID");
		let cart: BabyBjornCart | undefined;

		if (cartId) {
			try {
				cart = await CartService.fetchCartById(cartId);
			} catch (error) {
				if (isCommercetoolsHttpResponseError(error)) {
					if ([403, 404].includes(error.statusCode)) {
						LocalStorage.remove("CT_CART_ID");
						return;
					} else {
						throw error;
					}
				} else {
					throw error;
				}
			}

			if (validCart(cart)) {
				return cart;
			}
		}

		return;
	}, [validCart]);

	useEffect(() => {
		if (!validCart(checkoutState.cart)) {
			void loadCartFromLocalStorage().then((cart) => {
				checkoutDispatch({ type: "setCart", cart });
			});
		}
	}, [
		checkoutDispatch,
		checkoutState.cart,
		loadCartFromLocalStorage,
		validCart,
	]);

	const createCart = useCallback(async () => {
		if (!siteConfig.currency) {
			throw new Error("No currency set on site config state");
		}

		// If userCountry is not set, use the first country in the siteConfig.countries array
		// It will later be updated to the user's country when the userCountry is set
		const country = userCountry ?? siteConfig.countries[0];

		let cart = await CartService.createCartFromDraft({
			currency: siteConfig.currency,
			country,
			shippingAddress: { country },
			store: { typeId: "store", key: siteConfig.store },
			inventoryMode: "ReserveOnOrder",
		});

		const shopperIP = userLocationData?.ip;

		const customFields: Partial<
			Record<keyof BabyBjornOrderType["fields"], unknown>
		> = {};

		if (navigator.userAgent) {
			customFields.userAgent = navigator.userAgent;
		}

		if (shopperIP) {
			customFields.ipAddress = shopperIP;
		}

		try {
			cart = await CartService.setCartCustomFields(cart.id, customFields);
		} catch (error) {
			console.error("Error setting cart custom fields", error);
		}

		return cart;
	}, [
		siteConfig.countries,
		siteConfig.currency,
		siteConfig.store,
		userCountry,
		userLocationData?.ip,
	]);

	const refreshCart: CartServiceMethods["refreshCart"] =
		useCallback(async () => {
			const cart = await loadCartFromLocalStorage();
			checkoutDispatch({ type: "setCart", cart });
		}, [checkoutDispatch, loadCartFromLocalStorage]);

	const initExistingCart: CartServiceMethods["initExistingCart"] =
		useCallback(async () => {
			try {
				if (!checkoutState.cart) {
					await refreshCart();
				}
			} finally {
				checkoutDispatch({ type: "setCartLoading", loading: false });
			}
		}, [checkoutDispatch, checkoutState.cart, refreshCart]);

	const getOrCreateCart = useCallback(async (): Promise<BabyBjornCart> => {
		await initExistingCart();

		if (!validCart(checkoutState.cart)) {
			const cart = await loadCartFromLocalStorage();
			return cart ?? (await createCart());
		}

		return checkoutState.cart;
	}, [
		checkoutState.cart,
		createCart,
		initExistingCart,
		loadCartFromLocalStorage,
		validCart,
	]);

	const addCartPayment: CartServiceMethods["addCartPayment"] = useCallback(
		async (paymentId: string) => {
			const cart = await getOrCreateCart();

			const updatedCart = await CartService.addPayment(cart, paymentId);

			checkoutDispatch({ type: "setCart", cart: updatedCart });
		},

		[checkoutDispatch, getOrCreateCart],
	);

	const addItemsToCart: CartServiceMethods["addItemsToCart"] = useCallback(
		async (items, showToastOnAdd = true) => {
			const cart = await getOrCreateCart();

			const mappedItems = items.map<CartService.AddLineItemProps>(
				({ product, variant, additionalData, quantity }) => {
					const productTypeKey = product.productType.obj.key;

					if (customLineItemsFieldsTypeCheck.has(productTypeKey)) {
						const validationFunction =
							customLineItemsFieldsTypeCheck.get(productTypeKey);
						const dataValid =
							!validationFunction || validationFunction(additionalData);

						if (!dataValid) {
							throw new Error(
								`The supplied data for lineItem of type ${productTypeKey} is not valid.`,
							);
						}
					}

					if (!variant.id) {
						throw new Error("No variant id on supplied variant.");
					}

					const supplyChannel =
						variant.availability?.channels.find(
							(productVariantAvailablity) =>
								productVariantAvailablity.isOnStock,
						)?.channelId ?? undefined;

					const customLineItem = productTypeKey
						? customLineItemMap.get(productTypeKey)
						: undefined;

					// Handle both bundle flag and custom line items
					const custom = additionalData
						? isBundleItemData(additionalData)
							? {
									type: { typeId: "type" as const, key: "bundle-line-item" },
									fields: { bundleItem: additionalData.bundleItem },
								}
							: customLineItem
								? {
										type: { typeId: "type" as const, key: customLineItem },
										fields: additionalData,
									}
								: undefined
						: undefined;

					return {
						productId: product.id,
						variantId: variant.originalId,
						supplyChannel,
						custom,
						quantity,
					};
				},
			);

			const updatedCart = await CartService.addLineItems(cart, mappedItems);

			sendBrowserEvent("AddToCart", {
				cart: updatedCart,
				items: items.map(({ product, variant }) => ({ product, variant })),
			});

			pushCustomGTMEvent({
				type: "addToCart",
				data: {
					lineItems: updatedCart.lineItems.filter((lineItem) =>
						items.some(
							(item) =>
								lineItem.variant.id === Number(item.variant.id.split("_")[1]) &&
								lineItem.productId === item.product.id,
						),
					),
				},
			});

			checkoutDispatch({ type: "setCart", cart: updatedCart });

			if (showToastOnAdd && items.length === 1 && items[0]) {
				appDispatch({
					type: "setToasterProduct",
					toasterProduct: {
						product: items[0].product,
						variant: items[0].variant,
					},
				});
			}
		},
		[appDispatch, checkoutDispatch, getOrCreateCart],
	);

	const addGiftBoxItemToCart: CartServiceMethods["addGiftBoxItemToCart"] =
		useCallback(
			async (giftBoxProduct) => {
				const cart = await getOrCreateCart();

				//Verify if cart already has gift box item
				if (
					cart.lineItems.some(
						(lineItem) => lineItem.productId === giftBoxProduct.id,
					)
				) {
					return;
				}
				let supplyChannel;

				const variant = getGiftOptionsVariant(giftBoxProduct);

				if (variant.availability?.channels) {
					const channelsArray = Object.entries(variant.availability.channels);
					supplyChannel = channelsArray.find(
						([, value]) => value.isOnStock,
					)?.[0];
				}

				const updatedCart = await CartService.addLineItems(cart, [
					{
						productId: giftBoxProduct.id,
						variantId: variant.id,
						supplyChannel,
					},
				]);

				for (const lineItem of updatedCart.lineItems) {
					if (
						lineItem.variant.id === variant.id &&
						lineItem.productId === giftBoxProduct.id
					) {
						pushCustomGTMEvent({
							type: "addToCart",
							data: { lineItems: [{ ...lineItem, quantity: 1 }] },
						});
					}
				}

				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart],
		);

	/// Remove gift options if no other gift items are in the cart
	const removeGiftOptions = useCallback(
		async (cart: BabyBjornCart) => {
			let giftBoxId: string | undefined;
			let giftMessageId: string | undefined;

			// Retrieve the non-wrappable and non-messageable products list for the current store
			const storeNonWrappableProducts: string[] = isObjectKey(
				siteConfig.store,
				nonWrappableProducts,
			)
				? nonWrappableProducts[siteConfig.store]
				: [];

			const storeNonMessageableProducts: string[] = isObjectKey(
				siteConfig.store,
				nonMessageableProducts,
			)
				? nonMessageableProducts[siteConfig.store]
				: [];

			let hasWrappableProduct = false;
			let hasMessageableProduct = false;

			for (const lineItem of cart.lineItems) {
				if (lineItem.productKey === "gift-box") {
					giftBoxId = lineItem.id;
					continue;
				}
				if (lineItem.productKey === "gift-message") {
					giftMessageId = lineItem.id;
					continue;
				}

				const attributes = lineItem.variant.attributes;

				if (!attributes) {
					return cart;
				}

				const attribute = getAttribute(attributes, "product-family-identifier");

				if (
					typeof attribute?.value === "string" &&
					is.nonEmptyArray(storeNonMessageableProducts)
				) {
					const isNonWrappable = storeNonWrappableProducts.includes(
						attribute.value,
					);
					const isNonMessageable = storeNonMessageableProducts.includes(
						attribute.value,
					);

					if (!isNonWrappable) {
						hasWrappableProduct = true;
					}

					if (!isNonMessageable) {
						hasMessageableProduct = true;
					}
				}
			}

			if (!hasWrappableProduct && giftBoxId) {
				cart = await CartService.removeLineItemById(cart, giftBoxId);
			}

			if (!hasMessageableProduct && giftMessageId) {
				cart = await CartService.removeLineItemById(cart, giftMessageId);
			}

			return cart;
		},
		[siteConfig.store],
	);

	const addGiftMessageItemToCart: CartServiceMethods["addGiftMessageItemToCart"] =
		useCallback(
			async (giftMessageProduct) => {
				const cart = await getOrCreateCart();

				//Verify if cart already has gift message item
				if (
					cart.lineItems.some(
						(lineItem) => lineItem.productId === giftMessageProduct.id,
					)
				) {
					return;
				}
				let supplyChannel;
				if (giftMessageProduct.masterVariant.availability?.channels) {
					const channelsArray = Object.entries(
						giftMessageProduct.masterVariant.availability.channels,
					);
					supplyChannel = channelsArray.find(
						([, value]) => value.isOnStock,
					)?.[0];
				}

				const updatedCart = await CartService.addLineItems(cart, [
					{
						productId: giftMessageProduct.id,
						variantId: giftMessageProduct.masterVariant.id,
						supplyChannel,
						custom: {
							type: {
								typeId: "type",
								key: "gift-message-line-item",
							},
							fields: {
								message: "",
							},
						},
					},
				]);

				for (const lineItem of updatedCart.lineItems) {
					if (
						lineItem.variant.id === giftMessageProduct.masterVariant.id &&
						lineItem.productId === giftMessageProduct.id
					) {
						pushCustomGTMEvent({
							type: "addToCart",
							data: { lineItems: [{ ...lineItem, quantity: 1 }] },
						});
					}
				}

				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart],
		);

	const updateGiftMessageItemInCart: CartServiceMethods["updateGiftMessageItemInCart"] =
		useCallback(
			async (giftMessageProduct, giftMessageText) => {
				const cart = await getOrCreateCart();

				const changedGiftMessage = cart.lineItems.find(
					(lineItem) => lineItem.productId === giftMessageProduct.id,
				);

				if (!changedGiftMessage) {
					throw new Error("No gift message line item exists with that id");
				}

				if (changedGiftMessage.custom?.fields.message === giftMessageText) {
					return;
				}

				const updatedCart = await CartService.updateGiftMessage(
					cart,
					changedGiftMessage.id,
					giftMessageText,
				);
				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart],
		);

	const removeLineItemFromCart: CartServiceMethods["removeLineItemFromCart"] =
		useCallback(
			async (lineItemId: string) => {
				const cart = await getOrCreateCart();

				let updatedCart = await CartService.removeLineItemById(
					cart,
					lineItemId,
				);

				const removedLineItem = cart.lineItems.find(
					(lineItem) => lineItem.id === lineItemId,
				);
				if (removedLineItem) {
					updatedCart = await removeGiftOptions(updatedCart);
					pushCustomGTMEvent({
						type: "removeFromCart",
						data: { lineItem: removedLineItem },
					});
				}

				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart, removeGiftOptions],
		);

	const updateCartLineItemQuantity: CartServiceMethods["updateCartLineItemQuantity"] =
		useCallback(
			async (lineItemId: string, quantity: number) => {
				const cart = await getOrCreateCart();

				const changedLineItem = cart.lineItems.find(
					(lineItem) => lineItem.id === lineItemId,
				);

				if (!changedLineItem) {
					throw new Error("No line item exists with that id");
				}

				if (changedLineItem.quantity === quantity) {
					return;
				}

				let updatedCart = await CartService.changeLineItemQuantity(
					cart,
					lineItemId,
					quantity,
				);

				updatedCart = await removeGiftOptions(updatedCart);

				const removed = changedLineItem.quantity > quantity;

				if (removed) {
					pushCustomGTMEvent({
						type: "removeFromCart",
						data: {
							lineItem: {
								...changedLineItem,
								quantity: Math.abs(quantity - changedLineItem.quantity),
							},
						},
					});
				} else {
					pushCustomGTMEvent({
						type: "addToCart",
						data: {
							lineItems: [
								{
									...changedLineItem,
									quantity: Math.abs(quantity - changedLineItem.quantity),
								},
							],
						},
					});
				}

				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart, removeGiftOptions],
		);

	const removeCartPayment: CartServiceMethods["removeCartPayment"] =
		useCallback(
			async (paymentId: string) => {
				const cart = await getOrCreateCart();

				const updatedCart = await CartService.removePayment(cart, paymentId);

				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart],
		);

	const addCartAddresses: CartServiceMethods["addCartAddresses"] = useCallback(
		async (shippingAddress?: AddressDraft, billingAddress?: AddressDraft) => {
			const cart = await getOrCreateCart();
			const updatedCart = await CartService.addAddresses(
				cart,
				billingAddress,
				shippingAddress,
			);

			checkoutDispatch({ type: "setCart", cart: updatedCart });
		},
		[checkoutDispatch, getOrCreateCart],
	);

	const addCartDiscountCode: CartServiceMethods["addCartDiscountCode"] =
		useCallback(
			async (code: string) => {
				const cart = await getOrCreateCart();

				const updatedCart = await CartService.addDiscountCode(cart, code);

				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart],
		);

	// TODO Add support to remove one specific code
	const removeCartDiscountCode: CartServiceMethods["removeCartDiscountCode"] =
		useCallback(async () => {
			const cart = await getOrCreateCart();

			const updatedCart = await CartService.removeDiscountCode(cart);

			checkoutDispatch({ type: "setCart", cart: updatedCart });
		}, [checkoutDispatch, getOrCreateCart]);

	const setCartCustomField: CartServiceMethods["setCartCustomField"] =
		useCallback(
			async (key: keyof BabyBjornOrderType["fields"], value: unknown) => {
				const cart = await getOrCreateCart();

				const updatedCart = await CartService.setCartCustomField(
					cart.id,
					key,
					value,
				);

				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart],
		);

	const setShippingMethod: CartServiceMethods["setShippingMethod"] =
		useCallback(
			async (shippingMethod: ShippingMethod) => {
				const cart = await getOrCreateCart();

				const updatedCart = await CartService.setShippingMethod(
					cart,
					shippingMethod,
				);

				checkoutDispatch({ type: "setCart", cart: updatedCart });
			},
			[checkoutDispatch, getOrCreateCart],
		);

	const createOrderFromCart: CartServiceMethods["createOrderFromCart"] =
		useCallback(async () => {
			const cart = await getOrCreateCart();

			const updatedCart = await CartService.recalculateCart(cart);
			checkoutDispatch({ type: "setCart", cart: updatedCart });

			const order = await CartService.createOrderFromCart(updatedCart);

			sendBrowserEvent("OrderCreated", {
				order,
			});

			return order;
		}, [checkoutDispatch, getOrCreateCart]);

	const getSummaryPrices: CartServiceMethods["getSummaryPrices"] =
		useCallback(async () => {
			const cart = await getOrCreateCart();
			const updatedCart = await CartService.recalculateCart(cart);
			checkoutDispatch({ type: "setCart", cart: updatedCart });
			return getOrderSummaryPrices(updatedCart);
		}, [checkoutDispatch, getOrCreateCart]);

	const updateCartCountry = useCallback(
		async (country: TCountryCode) => {
			if (
				!countryUpdateInProgress &&
				checkoutState.cart &&
				checkoutState.cart.country !== country
			) {
				countryUpdateInProgress = true;

				let updatedCart = await CartService.addAddresses(
					checkoutState.cart,
					{ country },
					undefined,
				);

				updatedCart = await CartService.setCountry(updatedCart, country);

				checkoutDispatch({ type: "setCart", cart: updatedCart });

				countryUpdateInProgress = false;
			}
		},
		[checkoutDispatch, checkoutState.cart],
	);

	useEffect(() => {
		if (userCountry) {
			void updateCartCountry(userCountry);
		}
	}, [updateCartCountry, userCountry]);

	return {
		removeLineItemFromCart,
		updateCartLineItemQuantity,
		addItemsToCart,
		addGiftBoxItemToCart,
		addGiftMessageItemToCart,
		updateGiftMessageItemInCart,
		addCartAddresses,
		addCartPayment,
		removeCartPayment,
		addCartDiscountCode,
		removeCartDiscountCode,
		setShippingMethod,
		setCartCustomField,
		createOrderFromCart,
		initExistingCart,
		refreshCart,
		getSummaryPrices,
		cart: checkoutState.cart,
		cartLoading: checkoutState.cartLoading,
		hasCart: Boolean(checkoutState.cart),
	};
};
